import { parsePhoneNumberFromString, type CountryCode } from 'libphonenumber-js';
import { PAYS_TELEPHONE, indicatif } from '../lib/telephone';

/**
 * Un numero de telephone et son pays (2026-09-30).
 *
 * Le pays est un selecteur court a gauche du numero, la France par
 * defaut. On tape le numero comme on le lit — avec ou sans espaces, avec
 * ou sans indicatif — et le serveur le range sous sa forme stockee.
 *
 * A la sortie du champ, le numero se remet au format de son pays
 * (« 06 12 34 56 78 ») : on voit ce qu'on a saisi tel qu'il sera lu. Un
 * indicatif tape dans le numero fait aussi changer le pays — « +32 … »
 * avec FR passe en BE. Un numero que la bibliotheque ne reconnait pas
 * reste tel quel : c'est le serveur qui le refusera, avec son message.
 */
export function ChampTelephone({
  libelle = 'Téléphone',
  aide,
  numero,
  pays,
  onChange,
  required,
  autoComplete,
}: {
  libelle?: string;
  aide?: string;
  numero: string;
  pays: string;
  onChange: (numero: string, pays: string) => void;
  required?: boolean;
  autoComplete?: string;
}) {
  const connu = PAYS_TELEPHONE.some((p) => p.code === pays);

  const mettreEnForme = () => {
    const parse = parsePhoneNumberFromString(numero.trim(), pays as CountryCode);
    if (!parse || !parse.isValid() || !parse.country) return;
    onChange(parse.formatNational(), parse.country);
  };

  return (
    <div className="block">
      <span className="etiquette">{libelle}</span>
      <div className="flex gap-2">
        {/* Court : le code et l'indicatif suffisent, le numero prend le reste. */}
        <select
          className="champ w-[5.5rem] shrink-0 px-1 tabulaire"
          aria-label="Pays du numéro"
          value={pays}
          onChange={(e) => onChange(numero, e.target.value)}
        >
          {PAYS_TELEPHONE.map((p) => (
            <option key={p.code} value={p.code}>
              {p.code} {indicatif(p.code)}
            </option>
          ))}
          {/* Un pays hors de la liste courte (numero saisi avec un
              indicatif etranger) reste selectionnable tel quel. */}
          {!connu && (
            <option value={pays}>
              {pays} {indicatif(pays)}
            </option>
          )}
        </select>
        <input
          className="champ tabulaire min-w-0 flex-1"
          type="tel"
          inputMode="tel"
          placeholder={pays === 'FR' ? '06 12 34 56 78' : ''}
          value={numero}
          required={required}
          autoComplete={autoComplete}
          onChange={(e) => onChange(e.target.value, pays)}
          onBlur={mettreEnForme}
        />
      </div>
      {aide && <span className="mt-1 block text-xs text-encre-400">{aide}</span>}
    </div>
  );
}
